import { Checkbox, SelectInput, SelectInputItem, TextField, Tooltip } from '@ads/components-react';
import {
  AcademicCapIcon,
  InformationCircleIcon,
  ReceiptRefundIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { Controller, type FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, Textarea } from '@/shared/ui/atoms';
import { FormField } from '@/shared/ui/molecules';

import { usePlanEdit } from '../../../context/PlanEditContext';
import type { PlanFormValues } from '../../../hooks/usePlanFormSchema';
import {
  useScholarshipTypePercentage,
  useScholarshipTypesDropdown,
} from '../../../hooks/usePlanLookups';
import {
  getScholarshipStatusKey,
  SCHOLARSHIP_STATUS_VALUES,
} from '../../../types/scholarshipStatus';
import { formatPercentageInput, parsePercentageInput } from '../../../utils/maskedNumberInput';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';
import { PlanToggleBlock } from '../../molecules/PlanToggleBlock';

/** Grade de três colunas usada em todas as linhas de campo. */
function Row({ children }: { readonly children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">{children}</div>;
}

/**
 * Valor da opção "Sem bolsa" no select de status.
 *
 * Não é um status do backend — representa `scholarshipStatus: null`. Precisa de
 * um valor próprio porque o Radix Select trata a string vazia como "nada
 * selecionado", e um item com `value=""` não seria selecionável.
 */
const NO_SCHOLARSHIP = 'sem-bolsa';

/**
 * Aba "Detalhes" do formulário de edição de plano.
 *
 * Os dois blocos condicionais — boleto institucional e pagamento de terceiro —
 * vivem no **mesmo card** e usam o mesmo componente, porque são alternativas
 * paralelas de responsável pela cobrança. Separá-los em cards diferentes ou dar
 * a um deles cabeçalho próprio sugeriria uma hierarquia inexistente.
 */
export function PlanDetailsTab() {
  const { t } = useTranslation('individualPayment');
  const { form, isReadOnly } = usePlanEdit();
  const { control, watch, setValue, formState, getFieldState } = form;

  const { scholarshipTypes } = useScholarshipTypesDropdown();
  const scholarshipTypeId = watch('scholarshipTypeId') as number | null | undefined;
  const { percentage: suggestedPercentage } = useScholarshipTypePercentage(
    scholarshipTypeId ?? null,
  );

  const scholarshipStatus = watch('scholarshipStatus') as number | null | undefined;
  const scholarshipPercentage = watch('discountScholarshipPercentage') as number | null | undefined;

  /**
   * Tipo e percentual só existem depois que há uma solicitação de bolsa.
   *
   * Sem status, os dois campos pedem uma decisão sobre algo que não foi pedido —
   * num plano em criação, apareciam vazios e sem sentido.
   *
   * A segunda metade da condição é a salvaguarda: se um plano já gravado tiver
   * tipo ou percentual **sem** status — combinação que o legado permite —, os
   * campos continuam visíveis. Esconder um valor que existe é pior que exibir um
   * campo a mais: o operador não veria o que está prestes a salvar.
   */
  const showScholarshipDetails =
    scholarshipStatus != null || scholarshipTypeId != null || scholarshipPercentage != null;

  /**
   * Mensagem de erro traduzida de um campo.
   *
   * As mensagens do schema são **chaves de i18n**, não texto final — o schema é
   * derivado do gerado e não tem acesso ao `t()`.
   */
  const errorOf = (name: FieldPath<PlanFormValues>): string | undefined => {
    const message = getFieldState(name, formState).error?.message;
    // biome-ignore lint/suspicious/noExplicitAny: a chave vem do schema, fora do union tipado de t().
    return typeof message === 'string' ? String(t(message as any)) : undefined;
  };

  const text = (
    name: FieldPath<PlanFormValues>,
    label: string,
    options?: {
      readonly required?: boolean;
      readonly helper?: string;
      readonly placeholder?: string;
    },
  ) => {
    const error = errorOf(name);
    const helper = error ?? options?.helper;

    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <TextField
            label={options?.required ? `${label} *` : label}
            size="md"
            fullWidth
            // `fullWidth` estica o wrapper; o `w-full` no input garante que o
            // campo ocupe a coluna inteira do grid em vez da largura intrínseca.
            custom={{ root: 'w-full', input: 'w-full' }}
            disabled={isReadOnly}
            hasError={error !== undefined}
            {...(helper === undefined ? {} : { helper })}
            {...(options?.placeholder === undefined ? {} : { placeholder: options.placeholder })}
            value={typeof field.value === 'string' ? field.value : ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    );
  };

  /** Atalho: rótulo e placeholder saem das mesmas chaves de i18n. */
  const ph = (key: string) =>
    // biome-ignore lint/suspicious/noExplicitAny: chave montada a partir do bloco planEdit.placeholders.
    String(t(`planEdit.placeholders.${key}` as any));

  return (
    <>
      <PlanSectionCard title={t('planEdit.details.identification.title')} icon={TagIcon}>
        {/* Fora do grid de três colunas de propósito: a descrição é o campo que
            identifica o plano em listagens e no carnê, e ganha a largura toda. */}
        {text('description', t('planEdit.details.identification.description'), {
          required: true,
          helper: t('planEdit.details.identification.descriptionHelper'),
          placeholder: ph('description'),
        })}

        <Controller
          control={control}
          name="additionalInformation"
          render={({ field }) => (
            <FormField label={t('planEdit.details.identification.additionalInformation')}>
              <Textarea
                placeholder={t('planEdit.details.identification.additionalInformationPlaceholder')}
                rows={4}
                disabled={isReadOnly}
                value={typeof field.value === 'string' ? field.value : ''}
                onChange={field.onChange}
              />
            </FormField>
          )}
        />

        <div className="flex flex-col gap-quark">
          <span className="flex items-center gap-nano">
            <Controller
              control={control}
              name="hidePaymentPlan"
              render={({ field }) => (
                <Checkbox
                  checked={field.value === true}
                  size="sm"
                  disabled={isReadOnly}
                  label={t('planEdit.details.identification.hidePaymentPlan')}
                  onCheckedChange={checked => field.onChange(checked === true)}
                />
              )}
            />
            <Tooltip title={t('planEdit.details.identification.hidePaymentPlanTooltip')}>
              <InformationCircleIcon className="size-mini shrink-0 text-ctx-content-placeholder" />
            </Tooltip>
          </span>
        </div>
      </PlanSectionCard>

      <PlanSectionCard title={t('planEdit.details.scholarship.title')} icon={AcademicCapIcon}>
        <Row>
          <Controller
            control={control}
            name="scholarshipStatus"
            render={({ field }) => (
              <SelectInput
                label={t('planEdit.details.scholarship.status')}
                size="md"
                placeholder={t('planEdit.placeholders.scholarshipStatus')}
                fullWidth
                disabled={isReadOnly}
                // `null` exibe a opção "Sem bolsa", não o placeholder: ausência de
                // bolsa é uma informação sobre o plano, não um campo por preencher.
                value={field.value != null ? String(field.value) : NO_SCHOLARSHIP}
                onValueChange={value => {
                  if (value === NO_SCHOLARSHIP) {
                    field.onChange(null);
                    // Tipo e percentual pertenciam à solicitação que acabou de ser
                    // desfeita. Deixá-los para trás gravaria uma bolsa fantasma —
                    // e, como a seção volta a exibi-los quando existe valor, eles
                    // reapareceriam sozinhos logo depois de sumirem.
                    setValue('scholarshipTypeId', null, { shouldDirty: true });
                    setValue('discountScholarshipPercentage', null, { shouldDirty: true });
                    return;
                  }
                  field.onChange(Number(value));
                }}
              >
                {/* O Radix reserva a string vazia para "sem seleção", então a opção
                    precisa de um valor próprio em vez de `''`. */}
                <SelectInputItem custom={{ root: 'text-nowrap' }} value={NO_SCHOLARSHIP}>
                  {t('planEdit.details.scholarship.none')}
                </SelectInputItem>
                {SCHOLARSHIP_STATUS_VALUES.map(value => {
                  const key = getScholarshipStatusKey(value);
                  return (
                    <SelectInputItem
                      custom={{ root: 'text-nowrap' }}
                      key={value}
                      value={String(value)}
                    >
                      {key ? t(key) : String(value)}
                    </SelectInputItem>
                  );
                })}
              </SelectInput>
            )}
          />

          {showScholarshipDetails ? (
            <>
              <Controller
                control={control}
                name="scholarshipTypeId"
                render={({ field }) => (
                  <SelectInput
                    label={t('planEdit.details.scholarship.type')}
                    size="md"
                    fullWidth
                    disabled={isReadOnly}
                    value={field.value != null ? String(field.value) : ''}
                    onValueChange={value => field.onChange(value ? Number(value) : null)}
                  >
                    {scholarshipTypes.map(option => (
                      <SelectInputItem
                        custom={{ root: 'text-nowrap' }}
                        key={String(option.id)}
                        value={String(option.id)}
                      >
                        {option.label}
                      </SelectInputItem>
                    ))}
                  </SelectInput>
                )}
              />

              <Controller
                control={control}
                name="discountScholarshipPercentage"
                render={({ field }) => {
                  const percentage = typeof field.value === 'number' ? field.value : null;
                  // Mensagem só quando sai da faixa: texto de apoio permanente vira
                  // ruído num campo que quase sempre está correto.
                  const outOfRange = percentage !== null && (percentage < 0 || percentage > 100);

                  return (
                    <TextField
                      label={t('planEdit.details.scholarship.percentage')}
                      size="md"
                      fullWidth
                      custom={{ root: 'w-full', input: 'w-full' }}
                      placeholder={ph('percentage')}
                      disabled={isReadOnly}
                      inputMode="numeric"
                      hasError={outOfRange}
                      {...(outOfRange
                        ? { helper: t('planEdit.validation.scholarshipPercentageRange') }
                        : {})}
                      value={formatPercentageInput(percentage)}
                      onChange={event => field.onChange(parsePercentageInput(event.target.value))}
                    />
                  );
                }}
              />
            </>
          ) : null}
        </Row>

        {/* Também full width: é texto livre, e no legado ocupa a linha inteira. */}
        {text('scholarshipReason', t('planEdit.details.scholarship.reason'), {
          placeholder: ph('scholarshipReason'),
        })}

        {suggestedPercentage !== null ? (
          <Alert
            type="information"
            title={t('planEdit.details.scholarship.suggestedTitle')}
            description={t('planEdit.details.scholarship.suggestedHint', {
              value: suggestedPercentage,
            })}
          />
        ) : null}
      </PlanSectionCard>

      <PlanSectionCard title={t('planEdit.details.billing.title')} icon={ReceiptRefundIcon}>
        <div className="flex flex-col gap-quark">
          <Controller
            control={control}
            name="automaticGenerateInvoice"
            render={({ field }) => (
              <Checkbox
                checked={field.value === true}
                size="sm"
                disabled={isReadOnly}
                label={t('planEdit.details.billing.autoInvoice')}
                onCheckedChange={checked => field.onChange(checked === true)}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="payAsCompany"
          render={({ field }) => (
            <PlanToggleBlock
              title={t('planEdit.details.billing.payAsCompany')}
              hint={t('planEdit.details.billing.payAsCompanyHint')}
              checked={field.value === true}
              disabled={isReadOnly}
              onCheckedChange={field.onChange}
            >
              <Row>
                {text('companyName', t('planEdit.details.billing.companyName'), {
                  placeholder: ph('companyName'),
                })}
                {text('cnpj', t('planEdit.details.billing.cnpj'), { placeholder: ph('cnpj') })}
                {text('ie', t('planEdit.details.billing.ie'), { placeholder: ph('ie') })}
              </Row>
              <Row>
                {text('companyContactName', t('planEdit.details.billing.contactName'), {
                  placeholder: ph('contactName'),
                })}
                {text('companyContactEmail', t('planEdit.details.billing.contactEmail'), {
                  placeholder: ph('contactEmail'),
                })}
                {text('companyContactPhone', t('planEdit.details.billing.contactPhone'), {
                  placeholder: ph('contactPhone'),
                })}
              </Row>
              <Controller
                control={control}
                name="ieFree"
                render={({ field: ieField }) => (
                  <Checkbox
                    checked={ieField.value === true}
                    size="sm"
                    disabled={isReadOnly}
                    label={t('planEdit.details.billing.ieFree')}
                    onCheckedChange={checked => ieField.onChange(checked === true)}
                  />
                )}
              />
            </PlanToggleBlock>
          )}
        />

        <Controller
          control={control}
          name="thirdPayment"
          render={({ field }) => (
            <PlanToggleBlock
              title={t('planEdit.details.billing.thirdPayment')}
              hint={t('planEdit.details.billing.thirdPaymentHint')}
              checked={field.value === true}
              disabled={isReadOnly}
              onCheckedChange={field.onChange}
            >
              <Row>
                {text('thirdName', t('planEdit.details.billing.thirdName'), {
                  placeholder: ph('thirdName'),
                })}
                {text('thirdDocument', t('planEdit.details.billing.thirdDocument'), {
                  placeholder: ph('thirdDocument'),
                })}
                {text('thirdCode', t('planEdit.details.billing.thirdCode'), {
                  placeholder: ph('thirdCode'),
                })}
              </Row>
              <Row>
                {text('thirdEmail', t('planEdit.details.billing.thirdEmail'), {
                  placeholder: ph('thirdEmail'),
                })}
                {text('thirdPhone', t('planEdit.details.billing.thirdPhone'), {
                  placeholder: ph('thirdPhone'),
                })}
                <span />
              </Row>
              <span className="ts-paragraph-xxs !font-semibold text-ctx-content-placeholder">
                {t('planEdit.details.billing.addressTitle')}
              </span>
              <Row>
                {text('thirdAddress.zipCode', t('planEdit.details.billing.zipCode'), {
                  placeholder: ph('zipCode'),
                })}
                {text('thirdAddress.street', t('planEdit.details.billing.street'), {
                  placeholder: ph('street'),
                })}
                {text('thirdAddress.number', t('planEdit.details.billing.number'), {
                  placeholder: ph('number'),
                })}
              </Row>
              <Row>
                {text('thirdAddress.complement', t('planEdit.details.billing.complement'), {
                  placeholder: ph('complement'),
                })}
                {text('thirdAddress.district', t('planEdit.details.billing.district'), {
                  placeholder: ph('district'),
                })}
                {text('thirdAddress.city', t('planEdit.details.billing.city'), {
                  placeholder: ph('city'),
                })}
              </Row>
            </PlanToggleBlock>
          )}
        />
      </PlanSectionCard>
    </>
  );
}
